import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Copy, Mail, MessageCircle, Loader2 } from "lucide-react";
import { authHeaders, formatApiErrorDetail } from "@/components/schulungen/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const resetLink = (token) => `${window.location.origin}/schulungen/passwort-reset/${token}`;

export function ResetLinkDialog({ result, onClose }) {
  if (!result) return null;
  const link = resetLink(result.token);
  const text = `Hallo ${result.name},\n\nhier ist Ihr Link zum Zurücksetzen des Passworts für das Schulungsportal (1 Stunde gültig):\n${link}\n\nViele Grüße`;
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link kopiert"); } catch { toast.error("Kopieren nicht möglich"); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="reset-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2"><KeyRound className="h-5 w-5 text-cyan-700" />Passwort-Link für {result.name}</DialogTitle>
          <DialogDescription>Der Link ist 60 Minuten gültig und kann einmal verwendet werden. Bitte an {result.email} weitergeben.</DialogDescription>
        </DialogHeader>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-700 break-all" data-testid="reset-link-text">{link}</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={copy} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold" data-testid="reset-copy-btn"><Copy className="h-4 w-4" />Link kopieren</button>
          <a href={`mailto:${result.email}?subject=${encodeURIComponent("Passwort zurücksetzen – Schulungsportal")}&body=${encodeURIComponent(text)}`} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-cyan-50 hover:bg-cyan-100 text-cyan-700 text-sm font-semibold" data-testid="reset-mail-btn"><Mail className="h-4 w-4" />Per E-Mail</a>
          <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-green-50 hover:bg-green-100 text-green-700 text-sm font-semibold" data-testid="reset-wa-btn"><MessageCircle className="h-4 w-4" />Per WhatsApp</a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ResetLinkButton({ endpoint, angefordert, testId, onDone }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const create = async () => {
    setLoading(true);
    try { const { data } = await axios.post(`${API}${endpoint}`, {}, authHeaders(user)); setResult(data); onDone?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };
  return (
    <>
      <button onClick={create} disabled={loading} data-testid={testId}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold transition-colors ${angefordert ? "bg-amber-100 text-amber-800 hover:bg-amber-200 ring-1 ring-amber-300" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}{angefordert ? "Passwort-Reset angefordert!" : "Passwort-Link"}
      </button>
      <ResetLinkDialog result={result} onClose={() => setResult(null)} />
    </>
  );
}
